import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import type { DriveInfo, PipeChange } from "@tbm/shared";
import { api } from "../../lib/api";
import {
  localNaiveInputToUtcIso,
  useTimezone,
  utcToLocalNaive,
} from "../../lib/datetime";

/**
 * Pipe Schedule editor.
 *
 * Rows are auto-detected from the bypass-valve status + RPM/advance
 * rate (see apps/backend/src/pipeChanges/detector.ts) and numbered from
 * Pipe 1. The operator can edit any field; Save persists the table and
 * it's then used verbatim (new pipes still get appended as the drive
 * advances). Reset discards the saved edits and returns to pure
 * auto-detection. Typing a chainage into a manual row fills its
 * start/end times from the nearest reading.
 */

interface DraftRow {
  name: string;
  chainage: string;
  length: string;
  startTs: string | null;
  endTs: string | null;
  /** Per-pipe ram correction tick. Defaults to true for rows loaded
   *  without the field (pre-feature schedules + auto-detected pipes). */
  ramCorrectionEnabled: boolean;
  /** Per-pipe stage-1 length override. Empty string means "use the
   *  global default" (rendered as a placeholder in the input). */
  ramStage1Length: string;
}

function toDraft(changes: PipeChange[]): DraftRow[] {
  return changes.map((c) => ({
    name: c.name ?? "",
    chainage: round2(c.chainageM).toString(),
    // An open/in-progress pipe (lengthM 0) shows a blank length field,
    // not "0" — it reads as "not ended yet" rather than a zero-length
    // pipe. Completed pipes show their measured length.
    length: c.lengthM > 0 ? round2(c.lengthM).toString() : "",
    startTs: c.startTs,
    endTs: c.endTs,
    // Default the tick to ON unless the row was explicitly saved as
    // disabled. New rows + pre-feature persisted rows therefore start
    // ticked when the operator turns on global ram correction.
    ramCorrectionEnabled: c.ramCorrectionEnabled !== false,
    // Empty when null — the input shows the global value as
    // placeholder, so the operator can see what is being used without
    // freezing it into a per-pipe override.
    ramStage1Length:
      c.ramStage1LengthM != null ? round2(c.ramStage1LengthM).toString() : "",
  }));
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Default row name when the operator hasn't set one — just the
 *  pipe number. Matches the plot overlay's default label so what the
 *  operator sees on the chart matches the row they're looking at. */
function defaultName(idx: number): string {
  return String(idx + 1);
}

/** UTC ISO -> a datetime-local input value ("YYYY-MM-DDTHH:MM") in tz. */
function tsToInput(ts: string | null, tz: string): string {
  if (!ts) return "";
  return utcToLocalNaive(ts, tz)?.slice(0, 16) ?? "";
}

/** datetime-local input value -> UTC ISO, or null when blank. */
function inputToTs(input: string, tz: string): string | null {
  return input ? localNaiveInputToUtcIso(input, tz) : null;
}

/** Push duration (end minus start) as a compact label; "—" when unknown. */
function pushDuration(startTs: string | null, endTs: string | null): string {
  if (!startTs || !endTs) return "—";
  const ms = new Date(endTs).getTime() - new Date(startTs).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 1) return "<1m";
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

export function PipeScheduleEditor() {
  const qc = useQueryClient();
  const latestQuery = useQuery({ queryKey: ["latest"], queryFn: api.latest });
  const driveQuery = useQuery({ queryKey: ["drive"], queryFn: api.drive });
  const tz = useTimezone();

  const [draft, setDraft] = useState<DraftRow[]>([]);
  const [minAdvanceM, setMinAdvanceM] = useState<string>("2.8");
  const [dirty, setDirty] = useState(false);
  // Indices of rows the operator has touched in the current session.
  // Used only by the trailing-untouched-open-row drop in onSave: an
  // open last row that the operator has NOT touched is dropped so live
  // detection can own it, but a touched open row is kept verbatim
  // (validation will flag anything invalid). Cleared on save / reset.
  const [editedIdx, setEditedIdx] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // Per-row debounce timers for the chainage / length -> reading-time lookup.
  const lookupTimers = useRef<Record<number, number>>({});

  // Mirror the live draft. We update this ref SYNCHRONOUSLY inside
  // writeDraft below — not via a post-commit useEffect — so the
  // 600 ms debounced refresh and the onSave flush both read the
  // freshest value without waiting for React's render+effect cycle.
  // The previous useEffect-based mirror lagged by one render, which
  // meant a fast Save after a length edit persisted the old endTs.
  const draftRef = useRef<DraftRow[]>([]);

  // Single setter for the draft that updates state AND ref atomically.
  // Every mutation in this component goes through here, so async
  // callbacks (debounced refresh, onSave's await) never see a stale
  // snapshot.
  const writeDraft = (
    updater: ((rows: DraftRow[]) => DraftRow[]) | DraftRow[],
  ) => {
    const next =
      typeof updater === "function"
        ? (updater as (rows: DraftRow[]) => DraftRow[])(draftRef.current)
        : updater;
    draftRef.current = next;
    setDraft(next);
  };

  // Keep the draft + tuning knob in sync with the server while the
  // operator hasn't started editing. Once they edit (dirty), stop —
  // don't clobber their work on the next poll. Save / Reset clear it.
  useEffect(() => {
    if (dirty) return;
    if (latestQuery.data) writeDraft(toDraft(latestQuery.data.pipeChanges ?? []));
    if (driveQuery.data) {
      setMinAdvanceM(String(driveQuery.data.pipeDetection.minAdvanceM));
    }
    // writeDraft is stable across renders (it's a plain function
    // capturing refs), so omitting it from deps is safe and avoids
    // re-running this effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestQuery.data, driveQuery.data, dirty]);

  const saveMutation = useMutation({
    mutationFn: (args: { changes: PipeChange[]; minAdvanceM: number }) =>
      api.savePipeChanges(args.changes, args.minAdvanceM),
    onSuccess: async (data: DriveInfo) => {
      qc.setQueryData(["drive"], data);
      setMsg("Saved.");
      setError(null);
      qc.invalidateQueries({ queryKey: ["series-drive"] });
      // Hold off clearing `dirty` until the schedule refetch lands, so
      // the sync effect picks up the fresh server state instead of
      // momentarily reverting the draft to the stale /latest cache.
      await qc.refetchQueries({ queryKey: ["latest"] });
      setDirty(false);
      setEditedIdx(new Set());
      window.setTimeout(() => setMsg(null), 2500);
    },
    onError: (err: Error) => {
      setError(err.message);
      setMsg(null);
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => api.resetPipeSchedule(),
    onSuccess: (data: DriveInfo) => {
      qc.setQueryData(["drive"], data);
      qc.invalidateQueries({ queryKey: ["latest"] });
      qc.invalidateQueries({ queryKey: ["series-drive"] });
      setDirty(false);
      setEditedIdx(new Set());
      setMsg("Reset — re-detecting from data.");
      setError(null);
      window.setTimeout(() => setMsg(null), 2500);
    },
    onError: (err: Error) => {
      setError(err.message);
      setMsg(null);
    },
  });

  const markEdited = (idx: number) =>
    setEditedIdx((s) => {
      if (s.has(idx)) return s;
      const next = new Set(s);
      next.add(idx);
      return next;
    });

  const updateRow = (idx: number, patch: Partial<DraftRow>) => {
    setDirty(true);
    markEdited(idx);
    writeDraft((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  // Insert/remove shift every later row's array index by ±1, so any
  // already-tracked edited indices past the mutation point have to move
  // with their rows or the truncation on save would be off-by-one.
  const shiftEditedAfter = (from: number, delta: number) =>
    setEditedIdx((s) => {
      const next = new Set<number>();
      for (const k of s) next.add(k >= from ? k + delta : k);
      return next;
    });
  const removeRow = (idx: number) => {
    setDirty(true);
    markEdited(idx);
    shiftEditedAfter(idx + 1, -1);
    shiftLookupTimers(idx + 1, -1);
    writeDraft((rows) => rows.filter((_, i) => i !== idx));
  };
  const blankRow = (afterChainage: number): DraftRow => ({
    name: "",
    chainage: round2(afterChainage).toString(),
    // Blank, not "0" — a freshly added row has no length until the
    // operator types one (or, for the last row, it's left open).
    length: "",
    startTs: null,
    endTs: null,
    ramCorrectionEnabled: true,
    ramStage1Length: "",
  });
  // Insert a blank pipe row directly below row `idx` — for adding a
  // pipe the auto-detector missed (e.g. an interjack) mid-schedule.
  const insertRowAfter = (idx: number) => {
    setDirty(true);
    shiftEditedAfter(idx + 1, +1);
    shiftLookupTimers(idx + 1, +1);
    markEdited(idx + 1);
    writeDraft((rows) => {
      const prev = rows[idx];
      const chainage = prev
        ? Number(prev.chainage || 0) + Number(prev.length || 0)
        : 0;
      return [...rows.slice(0, idx + 1), blankRow(chainage), ...rows.slice(idx + 1)];
    });
    scheduleRefresh(idx + 1, true);
  };
  const addRow = () => {
    setDirty(true);
    const newIdx = draftRef.current.length;
    markEdited(newIdx);
    writeDraft((rows) => {
      const last = rows[rows.length - 1];
      const chainage = last
        ? Number(last.chainage || 0) + Number(last.length || 0)
        : 0;
      return [...rows, blankRow(chainage)];
    });
    scheduleRefresh(newIdx, true);
  };

  // Earliest time the TBM had reached `chainage`, via the chainage-time
  // endpoint. Returns null when the chainage hasn't been reached yet —
  // the caller uses that as the "length too long" signal.
  const lookupTs = async (chainage: number): Promise<string | null> => {
    if (!Number.isFinite(chainage) || chainage < 0) return null;
    try {
      const { ts } = await api.chainageTime(chainage);
      return ts;
    } catch {
      return null;
    }
  };

  // Re-fetch the row's start (when chainage changed) and/or end (always)
  // timestamps from the readings. The end-of-pipe chainage is start +
  // length; if the TBM hasn't reached it the lookup returns null and we
  // surface an error instead of clobbering the existing endTs.
  const refreshTimesForRow = async (idx: number, alsoStart: boolean) => {
    const row = draftRef.current[idx];
    if (!row) return;
    const ch = Number(row.chainage);
    const len = Number(row.length);
    if (!Number.isFinite(ch) || ch < 0) return;
    // A pipe only has an end once it has a POSITIVE length. A zero/
    // empty length means the pipe is still open (running) — it has no
    // end flag, so don't look one up (looking up ch+0 = ch would
    // wrongly set endTs equal to the start time).
    const hasLength = Number.isFinite(len) && len > 0;
    const endCh = hasLength ? ch + len : NaN;
    const [startTs, endTs] = await Promise.all([
      alsoStart ? lookupTs(ch) : Promise.resolve(null),
      hasLength ? lookupTs(endCh) : Promise.resolve(null),
    ]);
    if (hasLength && endCh > ch && endTs == null) {
      setError(
        `Row ${idx + 1}: length puts end at ${endCh.toFixed(2)} m — the TBM hasn't reached that chainage yet.`,
      );
      // Clear endTs so the field reflects the new (unreachable)
      // length. When the chainage itself was just edited (alsoStart),
      // startTs is set to whatever the lookup returned (valid time OR
      // null) so an unreachable chainage doesn't leave a stale start.
      writeDraft((rows) =>
        rows.map((r, i) => {
          if (i !== idx) return r;
          const next: DraftRow = { ...r, endTs: null };
          if (alsoStart) next.startTs = startTs;
          return next;
        }),
      );
      return;
    }
    setError(null);
    writeDraft((rows) =>
      rows.map((r, i) => {
        if (i !== idx) return r;
        const next: DraftRow = { ...r };
        if (alsoStart && startTs) next.startTs = startTs;
        // Positive length → set the looked-up end. No length → the
        // pipe is open, so clear any previously auto-filled end flag.
        if (hasLength) {
          if (endTs) next.endTs = endTs;
        } else {
          next.endTs = null;
        }
        return next;
      }),
    );
  };

  // Re-key any pending row-lookup timers when the array shifts (insert
  // or remove). Timer keys are row indices, so without this shift a
  // 600 ms refresh that was scheduled before the mutation would land on
  // the wrong row afterwards (the row that took its place).
  const shiftLookupTimers = (from: number, delta: number) => {
    const next: Record<number, number> = {};
    for (const [k, handle] of Object.entries(lookupTimers.current)) {
      const oldIdx = Number(k);
      if (delta < 0 && oldIdx === from - 1) {
        // The removed row itself — drop its pending timer entirely.
        window.clearTimeout(handle);
        continue;
      }
      next[oldIdx >= from ? oldIdx + delta : oldIdx] = handle;
    }
    lookupTimers.current = next;
  };

  const scheduleRefresh = (idx: number, alsoStart: boolean) => {
    const timers = lookupTimers.current;
    if (timers[idx]) window.clearTimeout(timers[idx]);
    timers[idx] = window.setTimeout(async () => {
      try {
        await refreshTimesForRow(idx, alsoStart);
      } finally {
        // Drop the handle so onSave's flush loop can tell when no
        // lookups are outstanding.
        delete lookupTimers.current[idx];
      }
    }, 600);
  };

  // When the operator manually edits a chainage and the cascade
  // shrinks/extends pipe N-1 to meet it, pipe N-1's endTs has to be
  // the LAST active-push moment before pipe N's start chainage —
  // NOT the time the TBM happened to first reach that chainage
  // (which is pipe N's startTs). Otherwise both flags land on the
  // same instant.
  const refreshPrevPipeEnd = async (prevIdx: number, nextStartChainage: number) => {
    if (!Number.isFinite(nextStartChainage) || nextStartChainage < 0) return;
    try {
      const { ts } = await api.lastActiveBeforeChainage(nextStartChainage);
      writeDraft((rows) =>
        rows.map((r, i) => (i === prevIdx ? { ...r, endTs: ts } : r)),
      );
    } catch {
      // Same UX contract as the other refreshes: swallow network
      // failures, leave the field untouched.
    }
  };

  const schedulePrevPipeEndRefresh = (
    prevIdx: number,
    nextStartChainage: number,
  ) => {
    const timers = lookupTimers.current;
    if (timers[prevIdx]) window.clearTimeout(timers[prevIdx]);
    timers[prevIdx] = window.setTimeout(async () => {
      try {
        await refreshPrevPipeEnd(prevIdx, nextStartChainage);
      } finally {
        delete lookupTimers.current[prevIdx];
      }
    }, 600);
  };

  // Wait until every scheduled chainage-time lookup has settled. The
  // save path calls this before reading the draft so a save fired
  // within the 600 ms debounce of a chainage edit doesn't persist the
  // stale (pre-refresh) start / end timestamps.
  const flushPendingLookups = async () => {
    while (Object.keys(lookupTimers.current).length > 0) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 80));
    }
  };

  const updateChainage = (idx: number, value: string) => {
    updateRow(idx, { chainage: value });
    // Schedule edits cascade backward by one row: a new start chainage
    // for pipe N is the same point as pipe N-1's end, so pipe N-1's
    // length is set to (new chainage of N) - (chainage of N-1) and its
    // endTs is re-fetched. Skipped when the typed value doesn't parse
    // or when the result would be negative — usually a mid-typing
    // transient that should clear once the operator finishes typing.
    if (idx >= 1) {
      const newCh = Number(value);
      const prev = draftRef.current[idx - 1];
      if (prev && Number.isFinite(newCh)) {
        const prevCh = Number(prev.chainage);
        if (Number.isFinite(prevCh)) {
          const newPrevLen = newCh - prevCh;
          if (newPrevLen >= 0) {
            markEdited(idx - 1);
            writeDraft((rows) =>
              rows.map((r, i) =>
                i === idx - 1
                  ? { ...r, length: round2(newPrevLen).toString() }
                  : r,
              ),
            );
            // Pipe N-1's endTs is looked up via "last active push
            // before chainage X" semantics, not the plain chainage
            // lookup — otherwise it would just be pipe N's startTs.
            schedulePrevPipeEndRefresh(idx - 1, newCh);
          }
        }
      }
    }
    scheduleRefresh(idx, true);
  };
  const updateLength = (idx: number, value: string) => {
    updateRow(idx, { length: value });
    scheduleRefresh(idx, false);
  };

  const onSave = async () => {
    const advM = Number(minAdvanceM);
    if (!Number.isFinite(advM) || advM <= 0) {
      setError("Min pipe length must be > 0");
      return;
    }
    if (Object.keys(lookupTimers.current).length > 0) {
      setMsg("Settling chainage lookups…");
      await flushPendingLookups();
      setMsg(null);
    }
    // Persist only up to the last row the operator has touched in this
    // session. Anything beyond that is dropped from the saved schedule
    // and re-detected from data on the next read (see cache.ts merge).
    // When no rows were touched (e.g. only minAdvanceM changed) we save
    // the full draft so the existing schedule is preserved.
    //
    // Read from draftRef rather than the `draft` closure: the await
    // above runs the debounced refreshes which call writeDraft to
    // update endTs/startTs. Those writes hit state + ref synchronously
    // (writeDraft) but the local `draft` variable in this handler is
    // frozen at the render where the click was bound, so it would
    // carry the pre-refresh timestamps.
    const current = draftRef.current;
    // Save the FULL draft. Earlier this slice was truncated to
    // max(editedIdx) + 1 so that detection re-ran on later rows after
    // any structural edit — but the side effect was that EVERY per-
    // pipe tag (name, ram correction, ram stage-1 length) on those
    // later rows got wiped, because the detector doesn't carry tags.
    // Persisted rows are authoritative; detection only fills in pipes
    // PAST the last persisted (see cache.ts buildPipeChanges). If the
    // operator wants to re-detect a stretch they explicitly remove
    // those rows or hit Reset.
    let cutoff = current.length;
    // Drop trailing OPEN rows the operator hasn't explicitly touched.
    // The detector emits the live push with lengthM = 0; persisting an
    // untouched open row would just freeze a snapshot of the live push,
    // so we let live detection own it instead. "Open" is defined by
    // length (<= 0), not by the end flag — a row saved before this
    // logic existed may carry an end time equal to its start. Touched
    // rows survive: if the operator typed something invalid we flag it
    // in validation rather than silently swallowing their work.
    while (cutoff > 0) {
      const idx = cutoff - 1;
      const r = current[idx];
      if (!r) break;
      if (editedIdx.has(idx)) break;
      const len = Number(r.length);
      const isOpen = !Number.isFinite(len) || len <= 0;
      if (isOpen) {
        cutoff--;
      } else {
        break;
      }
    }
    const toSave = current.slice(0, cutoff);
    const changes: PipeChange[] = [];
    for (let i = 0; i < toSave.length; i++) {
      const d = toSave[i]!;
      // Reject a blank chainage field BEFORE coercing to Number, since
      // Number("") === 0 would otherwise silently save the row with
      // start chainage 0 — losing whatever the operator had before
      // they cleared the field.
      if (d.chainage.trim() === "") {
        setError(`Row ${i + 1}: start chainage is required.`);
        return;
      }
      const chainageM = Number(d.chainage);
      const lengthRaw = Number(d.length);
      if (!Number.isFinite(chainageM) || chainageM < 0) {
        setError(`Row ${i + 1}: start must be ≥ 0`);
        return;
      }
      // A pipe is "open" (still running) when it has no positive
      // length — defined by LENGTH alone, so an auto-filled end time
      // can never block the save. Only the LAST row may be left open:
      // it persists as lengthM 0 / endTs null (the in-progress marker
      // cache.ts expects), and detection fills the end flag in once the
      // push completes. Every other row must carry a positive length.
      const isLast = i === toSave.length - 1;
      const hasLength = Number.isFinite(lengthRaw) && lengthRaw > 0;
      const open = isLast && !hasLength;
      const lengthM = open ? 0 : lengthRaw;
      const endTs = open ? null : d.endTs;
      if (!open && !hasLength) {
        // Completed pipe (any non-last row, or a last row the operator
        // gave a length) must have a positive length. minAdvanceM is a
        // DETECTION threshold, not a manual-entry constraint, so a
        // manually-saved short pipe (e.g. an interjack) below it is
        // allowed — only zero/empty is rejected, since a completed pipe
        // with no length pollutes the ram-stage boundaries and the
        // "active pipe length" lookup.
        setError(`Row ${i + 1}: length must be greater than 0, or remove the row.`);
        return;
      }
      // Monotonic chainage: each pipe must start at or after the
      // previous pipe's start. Non-monotonic schedules confuse every
      // downstream consumer (the JF ram-stage correction, the chart
      // overlay, the "active pipe at chainage" lookup).
      if (i > 0) {
        const prev = toSave[i - 1]!;
        const prevCh = Number(prev.chainage);
        if (Number.isFinite(prevCh) && chainageM < prevCh) {
          setError(
            `Row ${i + 1} chainage (${chainageM}) must be ≥ row ${i} chainage (${prevCh}).`,
          );
          return;
        }
      }
      // Per-row time order: end must not precede start.
      if (d.startTs && endTs) {
        const s = new Date(d.startTs).getTime();
        const e = new Date(endTs).getTime();
        if (Number.isFinite(s) && Number.isFinite(e) && e < s) {
          setError(`Row ${i + 1}: end time must be on or after the start time.`);
          return;
        }
      }
      // Per-pipe ram-stage overrides. Empty stage-1 field → null (use
      // the global default). The validation only fires when the field
      // is non-empty AND parses to a non-negative number.
      const ramStage1Trimmed = d.ramStage1Length.trim();
      let ramStage1LengthM: number | null = null;
      if (ramStage1Trimmed !== "") {
        const v = Number(ramStage1Trimmed);
        if (!Number.isFinite(v) || v < 0) {
          setError(`Row ${i + 1}: stage 1 length must be ≥ 0`);
          return;
        }
        ramStage1LengthM = v;
      }
      changes.push({
        pipeIndex: i + 1,
        name: d.name.trim() || null,
        chainageM,
        lengthM,
        startTs: d.startTs,
        endTs,
        ramCorrectionEnabled: d.ramCorrectionEnabled,
        ramStage1LengthM,
      });
    }
    setError(null);
    saveMutation.mutate({ changes, minAdvanceM: advM });
  };

  const busy = saveMutation.isPending || resetMutation.isPending;

  // The Ram corr / Stage 1 columns appear ONLY when the operator has
  // turned on the global ram-stage correction in the preprocessing
  // drawer. The placeholder for the Stage 1 input shows the current
  // global value, so the operator can see what is being used without
  // freezing a per-pipe override.
  const ramOn = driveQuery.data?.ramStageCorrection.enabled === true;
  const ramStage1Global =
    driveQuery.data?.ramStageCorrection.stage1LengthM ?? 1.665;
  const baseColCount = 7;
  const colCount = baseColCount + (ramOn ? 2 : 0);

  return (
    <div className="flex flex-col gap-5">
      <p className="text-[11.5px] text-muted leading-snug">
        Pipe changes are auto-detected from the bypass-valve status (a sustained ERROR = a
        pipe install); RPM and AR going non-zero set the start/finish times. Edit the table
        as required — typing a chainage into a manual row fills its start/end from the
        nearest reading.
      </p>

      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-[10.5px] uppercase tracking-wider text-dim">
            Min pipe length (m)
          </span>
          <input
            className="w-32 bg-surface2 border border-border1 rounded px-2 py-1 mono-nums"
            type="number"
            step="0.01"
            value={minAdvanceM}
            onChange={(e) => { setDirty(true); setMinAdvanceM(e.target.value); }}
          />
        </label>
      </div>

      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-dim text-center">
            <th className="font-medium pb-2 pr-2 w-[5rem]">Item</th>
            <th className="font-medium pb-2 pr-2 w-[6rem]">Start (m)</th>
            <th className="font-medium pb-2 pr-2 w-[6rem]">Length (m)</th>
            <th className="font-medium pb-2 pr-2">Start time</th>
            <th className="font-medium pb-2 pr-2">End time</th>
            <th className="font-medium pb-2 pr-2 w-[4.5rem]">Push time</th>
            {ramOn && (
              <>
                <th className="font-medium pb-2 pr-2 w-[4.5rem]">Ram corr</th>
                <th className="font-medium pb-2 pr-2 w-[6rem]">Stage 1 (m)</th>
              </>
            )}
            <th className="pb-2 w-12" />
          </tr>
        </thead>
        <tbody>
          {draft.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="py-3 text-[11.5px] text-muted italic">
                No pipes detected yet — waiting for the first push.
              </td>
            </tr>
          ) : (
            draft.map((row, idx) => (
              <tr key={idx} className="border-t border-border1">
                <td className="py-1.5 pr-2 align-middle">
                  <input
                    className="w-full bg-surface2 border border-border1 rounded px-2 py-1"
                    type="text"
                    value={row.name}
                    placeholder={defaultName(idx)}
                    onChange={(e) => updateRow(idx, { name: e.target.value })}
                  />
                </td>
                <td className="py-1.5 pr-2 align-middle">
                  <input
                    className="w-full bg-surface2 border border-border1 rounded px-2 py-1 mono-nums"
                    type="number"
                    step="0.01"
                    value={row.chainage}
                    onChange={(e) => updateChainage(idx, e.target.value)}
                  />
                </td>
                <td className="py-1.5 pr-2 align-middle">
                  <input
                    className="w-full bg-surface2 border border-border1 rounded px-2 py-1 mono-nums"
                    type="number"
                    step="0.01"
                    value={row.length}
                    onChange={(e) => updateLength(idx, e.target.value)}
                  />
                </td>
                <td className="py-1.5 pr-2 align-middle">
                  <input
                    className="w-full bg-surface2 border border-border1 rounded px-2 py-1 mono-nums"
                    type="datetime-local"
                    value={tsToInput(row.startTs, tz)}
                    onChange={(e) => updateRow(idx, { startTs: inputToTs(e.target.value, tz) })}
                  />
                </td>
                <td className="py-1.5 pr-2 align-middle">
                  <input
                    className="w-full bg-surface2 border border-border1 rounded px-2 py-1 mono-nums"
                    type="datetime-local"
                    value={tsToInput(row.endTs, tz)}
                    onChange={(e) => updateRow(idx, { endTs: inputToTs(e.target.value, tz) })}
                  />
                </td>
                <td className="py-1.5 pr-2 align-middle text-center mono-nums text-muted">
                  {pushDuration(row.startTs, row.endTs)}
                </td>
                {ramOn && (
                  <>
                    <td className="py-1.5 pr-2 align-middle text-center">
                      <input
                        type="checkbox"
                        className="accent-wb"
                        checked={row.ramCorrectionEnabled}
                        onChange={(e) =>
                          updateRow(idx, { ramCorrectionEnabled: e.target.checked })
                        }
                      />
                    </td>
                    <td className="py-1.5 pr-2 align-middle">
                      <input
                        className="w-full bg-surface2 border border-border1 rounded px-2 py-1 mono-nums disabled:opacity-40"
                        type="number"
                        step="0.01"
                        min="0"
                        value={row.ramStage1Length}
                        placeholder={String(ramStage1Global)}
                        disabled={!row.ramCorrectionEnabled}
                        onChange={(e) =>
                          updateRow(idx, { ramStage1Length: e.target.value })
                        }
                      />
                    </td>
                  </>
                )}
                <td className="py-1.5 pl-2 align-middle text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => insertRowAfter(idx)}
                    className="text-muted hover:text-text mr-2"
                    title="Insert a pipe row below"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="text-muted hover:text-wb"
                    title="Remove this row"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={addRow}
          className="px-2 py-1 text-[11px] uppercase tracking-wider text-muted hover:text-text bg-surface2 border border-border1 rounded"
        >
          + add row
        </button>
      </div>

      <div className="flex items-center gap-3 border-t border-border1 pt-3">
        <button
          type="button"
          onClick={onSave}
          disabled={busy || draft.length === 0}
          className={clsx(
            "px-3 py-1.5 text-[11px] uppercase tracking-wider rounded transition-colors",
            busy || draft.length === 0
              ? "bg-surface3 text-dim cursor-not-allowed"
              : "bg-wb text-white hover:bg-wbHot",
          )}
        >
          {saveMutation.isPending ? "Saving…" : "Save schedule"}
        </button>
        <button
          type="button"
          onClick={() => resetMutation.mutate()}
          disabled={busy}
          className={clsx(
            "px-3 py-1.5 text-[11px] uppercase tracking-wider rounded transition-colors",
            busy
              ? "bg-surface3 text-dim cursor-not-allowed"
              : "bg-surface2 border border-border1 text-muted hover:text-text",
          )}
        >
          {resetMutation.isPending ? "Resetting…" : "Reset"}
        </button>
        {error && <span className="text-[11px] text-wb">{error}</span>}
        {msg && <span className="text-[11px] text-muted">{msg}</span>}
      </div>
    </div>
  );
}
